import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';

import { BehaviorSubject, combineLatest, EMPTY, Observable, ReplaySubject } from 'rxjs';
import {
    catchError,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    startWith,
    switchMap,
    takeUntil,
    tap
} from 'rxjs/operators';

import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { ShPageHeaderComponent } from '../../../elements/components/page-header/page-header.component';
import { ShDisplaySettingsBarComponent } from '../../../overviews/generic-overview/components/display-settings-bar/display-settings-bar.component';
import { PaginatedResults } from '../../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { PaginationInfo } from '../../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { ShPaginationComponent } from '../../../overviews/generic-overview/components/pagination/pagination.component';
import { MetabaseReport } from '../../entities/report.entity';
import { ShReportsSandboxService } from '../../sandbox/reports-sandbox.service';

@Component({
    selector: 'sh-reports-overview-container',
    templateUrl: './reports-overview-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
    ShPageHeaderComponent,
    ShDisplaySettingsBarComponent,
    RouterLink,
    ShPaginationComponent,
    AsyncPipe,
    TranslateModule
]
})
export class ShReportsOverviewContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    private readonly DEFAULT_PAGE_SIZE: number = 10;
    private readonly refreshable$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    private readonly collection$: ReplaySubject<string | undefined> = new ReplaySubject(1);

    public readonly COLUMN_COUNT: number = 1;

    public reports$!: Observable<PaginatedResults<MetabaseReport>>;

    public request$!: Observable<PaginationInfo>;
    public currentPageSize$!: Observable<number>;
    public paginationInfo$!: Observable<PaginationInfo>;

    constructor(
        private readonly sandbox: ShReportsSandboxService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly router: Router
    ) {
        super();
    }

    ngOnInit(): void {
        this.setupSourceStream();
        this.initPagedResult();
        this.initCurrentPageSize();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.refreshable$.complete();
        this.collection$.complete();
    }

    refreshList(): void {
        this.refreshable$.next(true);
    }

    onPaginationInfoChanged(paginationInfo: PaginationInfo): void {
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {
                ...this.activatedRoute.snapshot.queryParams,
                page: paginationInfo.page,
                size: paginationInfo.resultsPerPage
            }
        });
    }

    private setupSourceStream(): void {
        this.request$ = this.activatedRoute.queryParams.pipe(
            map(queryParams => this.mapParamsToSearch(queryParams)),
            distinctUntilChanged(isEqual),
            takeUntil(this.destroy$)
        );

        this.reports$ = combineLatest([this.refreshable$, this.request$]).pipe(
            tap(_ => this.startLoading()),
            switchMap(([_, recordSearch]) =>
                this.sandbox.getReports(recordSearch).pipe(catchError(_ => this.showError()))
            ),
            tap(_ => this.stopLoading()),
            shareReplay(1)
        );
    }

    private initPagedResult(): void {
        this.paginationInfo$ = this.reports$.pipe(map(paginatedResults => paginatedResults.paginationInfo));
    }

    private initCurrentPageSize(): void {
        this.currentPageSize$ = this.activatedRoute.queryParams.pipe(
            filter((params: Params | null): params is Params => params && params.size),
            map(params => Number(params.size)),
            startWith(this.DEFAULT_PAGE_SIZE)
        );
    }

    private startLoading(): void {
        this.error$.next(false);
        this.loading$.startLoading();
    }

    private stopLoading(): void {
        this.loading$.stopLoading();
    }

    private showError(): Observable<never> {
        this.loading$.stopLoading();
        this.error$.next(true);
        return EMPTY;
    }

    private mapParamsToSearch(params: Params | null): PaginationInfo {
        const { page, size } = params || {};

        return {
            page: (page && size && page * size) || 0,
            resultsPerPage: size || this.DEFAULT_PAGE_SIZE
        };
    }
}
